/** `$field:<fieldId>` token grammar, shared by defaultExpression and entry title templates. */
export const FIELD_TOKEN_PREFIX = '$field:';

const FIELD_TOKEN = /\$field:([A-Za-z0-9_-]+)/g;

export const fieldTokenId = (expression: string): string | undefined =>
  expression.startsWith(FIELD_TOKEN_PREFIX)
    ? expression.slice(FIELD_TOKEN_PREFIX.length) || undefined
    : undefined;

export type TemplateSegment =
  | { kind: 'field'; fieldId: string }
  | { kind: 'text'; text: string };

/** Splits a template into its field tokens and the literal text between them. */
export function parseTemplateSegments(text: string): TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  const pattern = new RegExp(FIELD_TOKEN.source, 'g');
  let cursor = 0;
  let match = pattern.exec(text);

  while (match) {
    const literal = text.slice(cursor, match.index).trim();
    if (literal) segments.push({ kind: 'text', text: literal });
    segments.push({ kind: 'field', fieldId: match[1] });
    cursor = match.index + match[0].length;
    match = pattern.exec(text);
  }

  const tail = text.slice(cursor).trim();
  if (tail) segments.push({ kind: 'text', text: tail });
  return segments;
}

export const buildTemplate = (segments: TemplateSegment[]): string =>
  segments
    .map((segment) =>
      segment.kind === 'field'
        ? `${FIELD_TOKEN_PREFIX}${segment.fieldId}`
        : segment.text
    )
    .join(' ');

/** Replaces every token in `text`, reporting how many resolved to a non-empty value. */
export function interpolateFieldTokens(
  text: string,
  resolve: (fieldId: string) => string
): { text: string; resolvedCount: number } {
  let resolvedCount = 0;

  const interpolated = text.replace(FIELD_TOKEN, (_match, fieldId: string) => {
    const value = resolve(fieldId);
    if (value) resolvedCount += 1;
    return value;
  });

  return {
    text: interpolated.replace(/\s+/g, ' ').trim(),
    resolvedCount,
  };
}
